import type { Location, RegionalPricingTable, RegionalResolutionResult } from "../types/index.js";

function normalize(value: string): string {
  return value.trim().toLowerCase();
}

function fsaPrefix(postalCode: string | undefined): string | null {
  if (!postalCode) return null;
  const compact = postalCode.replace(/\s+/g, "").toUpperCase();
  return compact.length >= 3 ? compact.slice(0, 3) : null;
}

/**
 * Resolves the regional cost multiplier for a project location.
 *
 * Resolution order: postal FSA (most specific) -> city -> province ->
 * national baseline. Each tier is only used if the table actually has an
 * entry for it - not every province has city or postal data, and that's
 * expected (see docs/regional-pricing.md). Whenever resolution has to
 * fall back further than the most specific tier the input could support,
 * `isFallback` is true and `resolvedTier` says exactly which tier was
 * used, so callers can surface that honestly instead of implying
 * precision the data doesn't have.
 */
export function resolveRegionalMultiplier(
  location: Location,
  table: RegionalPricingTable
): RegionalResolutionResult {
  const fsa = fsaPrefix(location.postalCode);
  if (fsa) {
    const postalMatch = table.postalEntries.find(
      (e) => e.province === location.province && e.postalFsaPrefix === fsa
    );
    if (postalMatch) {
      return { multiplier: postalMatch.multiplier, resolvedTier: "postal", isFallback: false, matchedEntry: postalMatch };
    }
  }

  if (location.city) {
    const cityMatch = table.cityEntries.find(
      (e) => e.province === location.province && e.city !== null && normalize(e.city) === normalize(location.city!)
    );
    if (cityMatch) {
      const isFallback = fsa !== null; // asked for postal precision, only got city
      return { multiplier: cityMatch.multiplier, resolvedTier: "city", isFallback, matchedEntry: cityMatch };
    }
  }

  const provinceMatch = table.provinceEntries.find((e) => e.province === location.province);
  if (provinceMatch) {
    const isFallback = fsa !== null || !!location.city;
    return { multiplier: provinceMatch.multiplier, resolvedTier: "province", isFallback, matchedEntry: provinceMatch };
  }

  return {
    multiplier: table.nationalBaselineMultiplier,
    resolvedTier: "national_fallback",
    isFallback: true,
    matchedEntry: null
  };
}
